import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mic, MicOff, X, Sparkles, AlertCircle, Headphones, Send } from 'lucide-react'
import { useSessioneVoce, type StatoSessione } from '../../hooks/useSessioneVoce'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { WaveformMicrofono } from './WaveformMicrofono'
import { controllaSupportoBrowser, BrowserNonSupportato } from './ControlloBrowser'
import { etichettaDifficolta, varianteDifficolta } from '../../lib/format'
import styles from './SessioneVoce.module.css'

interface Props {
  leadProfileId: string
  leadNome: string
  leadRuolo: string
  leadSettore: string
  leadDifficolta: number
  /** Obiettivo della chiamata scelto dall'SDR (opzionale). Calibra l'AI. */
  obiettivoScelto?: string
  onChiudi: () => void
}

/**
 * Overlay fullscreen per la sessione vocale via microfono browser.
 *
 * Flusso:
 *  1. Controllo compatibilità browser → se fallisce mostra messaggio
 *  2. Mostra info lead e pulsante "Avvia sessione"
 *  3. Avvio: richiesta microfono + connessione WS
 *  4. Schermata sessione attiva: trascrizione + waveform + risposta lead
 *  5. Click "Termina" o close → manda 'fine' al server, salva, mostra feedback
 *  6. Click "Vai al feedback" → naviga al dettaglio sessione
 */
export function SessioneVoce({
  leadProfileId, leadNome, leadRuolo, leadSettore, leadDifficolta, obiettivoScelto, onChiudi,
}: Props) {
  const navigate = useNavigate()
  const compatibilita = controllaSupportoBrowser()

  const {
    stato, trascrizione, rispostaLead, volumeMicrofono,
    sessionId, errore, feedbackInBackground, messaggioChiusura,
    avvia, termina, forzaTurno,
  } = useSessioneVoce(leadProfileId, obiettivoScelto)

  const [testoTypewriter, setTestoTypewriter] = useState('')

  // Typewriter effect sulla risposta del lead
  useEffect(() => {
    if (!rispostaLead) {
      setTestoTypewriter('')
      return
    }
    setTestoTypewriter('')
    let i = 0
    const id = setInterval(() => {
      i++
      setTestoTypewriter(rispostaLead.slice(0, i))
      if (i >= rispostaLead.length) clearInterval(id)
    }, 25)
    return () => clearInterval(id)
  }, [rispostaLead])

  return (
    <div className={styles.overlay}>
      <button className={styles.chiudi} onClick={onChiudi} aria-label="Chiudi">
        <X size={20} />
      </button>

      <div className={styles.contenitore}>
        {!compatibilita.supportato ? (
          <BrowserNonSupportato risultato={compatibilita} />
        ) : stato === 'inattiva' || stato === 'inizializzazione' ? (
          <PrePartenza
            leadNome={leadNome}
            leadRuolo={leadRuolo}
            leadSettore={leadSettore}
            leadDifficolta={leadDifficolta}
            obiettivoScelto={obiettivoScelto}
            inizializzazione={stato === 'inizializzazione'}
            errore={errore}
            onAvvia={avvia}
            onAnnulla={onChiudi}
          />
        ) : stato === 'completata' ? (
          <Completata
            sessionId={sessionId}
            feedbackInBackground={feedbackInBackground}
            messaggio={messaggioChiusura}
            onChiudi={onChiudi}
            onVaiAllaSessione={() => {
              if (sessionId) navigate(`/app/sessioni/${sessionId}`)
            }}
            onVaiAlleSessioni={() => navigate('/app/sessioni')}
          />
        ) : stato === 'errore' ? (
          <ErroreView messaggio={errore ?? 'Errore sconosciuto'} onChiudi={onChiudi} />
        ) : (
          <SessioneAttiva
            leadNome={leadNome}
            leadRuolo={leadRuolo}
            stato={stato}
            trascrizione={trascrizione}
            rispostaTypewriter={testoTypewriter}
            volumeMicrofono={volumeMicrofono}
            onTermina={() => termina()}
            onForzaTurno={forzaTurno}
          />
        )}
      </div>
    </div>
  )
}

// ─── Schermata pre-partenza ──────────────────────────────────────────
function PrePartenza({
  leadNome, leadRuolo, leadSettore, leadDifficolta, obiettivoScelto,
  inizializzazione, errore, onAvvia, onAnnulla,
}: {
  leadNome: string; leadRuolo: string; leadSettore: string; leadDifficolta: number
  obiettivoScelto?: string
  inizializzazione: boolean; errore: string | null
  onAvvia: () => Promise<void>; onAnnulla: () => void
}) {
  return (
    <div className={styles.prePartenza}>
      <div className={styles.leadIntestazione}>
        <div className={styles.leadAvatar}>
          <Mic size={40} />
        </div>
        <h2 className={styles.leadNome}>{leadNome}</h2>
        <div className={styles.leadInfo}>{leadRuolo} · {leadSettore}</div>
        <div style={{ marginTop: 10 }}>
          <Badge variante={varianteDifficolta(leadDifficolta)}>
            {etichettaDifficolta(leadDifficolta)}
          </Badge>
        </div>
      </div>

      {obiettivoScelto && (
        <div style={{
          padding: '12px 16px',
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          borderRadius: 'var(--radius-md)',
          textAlign: 'center',
          fontSize: 13,
        }}>
          <strong style={{ display: 'block', marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            🎯 Obiettivo
          </strong>
          {obiettivoScelto}
        </div>
      )}

      <div className={styles.avvisoCuffie}>
        <Headphones size={18} />
        <span>Per una migliore esperienza usa le cuffie o abbassa il volume del PC.</span>
      </div>

      {errore && (
        <div className={styles.errore}>
          <AlertCircle size={16} />
          <span>{errore}</span>
        </div>
      )}

      <div className={styles.azioni}>
        {/* Annulla SEMPRE cliccabile — durante init è l'unico escape */}
        <Button variante="ghost" onClick={onAnnulla}>
          {inizializzazione ? 'Interrompi' : 'Annulla'}
        </Button>
        <Button
          taglia="lg"
          iconaSinistra={<Mic size={18} />}
          onClick={() => { void onAvvia() }}
          caricamento={inizializzazione}
          disabled={inizializzazione}
        >
          {inizializzazione ? 'Connessione…' : 'Inizia la conversazione'}
        </Button>
      </div>

      <div className={styles.suggerimento}>
        Quando sei pronto, parla normalmente. Il lead risponderà appena finisci la frase.
      </div>
    </div>
  )
}

// ─── Sessione attiva ─────────────────────────────────────────────────
function SessioneAttiva({
  leadNome, leadRuolo, stato, trascrizione, rispostaTypewriter,
  volumeMicrofono, onTermina, onForzaTurno,
}: {
  leadNome: string; leadRuolo: string; stato: StatoSessione
  trascrizione: string; rispostaTypewriter: string
  volumeMicrofono: number; onTermina: () => void
  onForzaTurno: () => void
}) {
  const inAscolto = stato === 'ascolto' || stato === 'pronto'
  // Mostra "Invia frase" solo se l'utente sta accumulando testo e il
  // backend è in ascolto: dà controllo manuale per saltare i 4s di
  // attesa della finestra di silenzio.
  const mostraInvio = inAscolto && trascrizione.trim().length > 0

  return (
    <div className={styles.sessioneAttiva}>
      <div className={styles.intestazioneCompatta}>
        <strong>{leadNome}</strong>
        <span> · {leadRuolo}</span>
        <Badge variante={inAscolto ? 'success' : 'accent'}>
          {etichettaStato(stato)}
        </Badge>
      </div>

      <div className={styles.zonaLead}>
        <div className={styles.leadAvatarGrande}>
          {stato === 'lead_parla' ? <Sparkles size={48} /> : <Mic size={48} />}
        </div>
        <div className={styles.rispostaTesto}>
          {rispostaTypewriter || (
            <span style={{ opacity: 0.5, fontStyle: 'italic' }}>
              {stato === 'elaborazione' ? '…sta riflettendo…' : 'In attesa che tu parli…'}
            </span>
          )}
        </div>
      </div>

      <div className={styles.trascrizione}>
        {trascrizione ? (
          <><strong>Tu:</strong> {trascrizione}</>
        ) : (
          <span style={{ opacity: 0.5 }}>Tu: …</span>
        )}
      </div>

      <div className={styles.waveformWrap}>
        <WaveformMicrofono volume={volumeMicrofono} attivo={inAscolto} />
      </div>

      <div className={styles.azioni}>
        {mostraInvio && (
          <Button
            variante="secondary"
            taglia="lg"
            iconaSinistra={<Send size={16} />}
            onClick={onForzaTurno}
            title="Invia subito la frase senza aspettare i 4 secondi di silenzio"
          >
            Invia frase
          </Button>
        )}
        <Button
          variante="danger"
          taglia="lg"
          iconaSinistra={<MicOff size={18} />}
          onClick={onTermina}
        >
          Termina sessione
        </Button>
      </div>
    </div>
  )
}

// ─── Schermata completata ────────────────────────────────────────────
// Due varianti:
//  - feedback_in_background=true  → spinner + "Vai alle sessioni" (l'utente
//    sarà notificato dal badge rosso quando il feedback è pronto)
//  - feedback già pronto          → "Vai al feedback" classico
function Completata({
  sessionId, feedbackInBackground, messaggio,
  onChiudi, onVaiAllaSessione, onVaiAlleSessioni,
}: {
  sessionId: string | null
  feedbackInBackground: boolean
  messaggio: string | null
  onChiudi: () => void
  onVaiAllaSessione: () => void
  onVaiAlleSessioni: () => void
}) {
  if (feedbackInBackground) {
    return (
      <div className={styles.completata}>
        <div
          style={{
            width: 56, height: 56,
            border: '3px solid var(--border)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px',
          }}
        />
        <h2>Sessione completata!</h2>
        <p className={styles.sottotitolo}>
          {messaggio ?? 'Il feedback è in elaborazione. Sarà pronto a breve (di solito entro 30–60 secondi).'}
          <br />
          Ti avviseremo con un badge rosso su <strong>Sessioni</strong>.
        </p>
        <div className={styles.azioni}>
          <Button variante="ghost" onClick={onChiudi}>Chiudi</Button>
          <Button onClick={onVaiAlleSessioni}>Vai alle sessioni →</Button>
        </div>
      </div>
    )
  }

  // Feedback già pronto / sessione troppo breve per generarlo
  return (
    <div className={styles.completata}>
      <div className={styles.iconaSuccess}>
        <Sparkles size={48} />
      </div>
      <h2>Sessione completata</h2>
      <p className={styles.sottotitolo}>
        {messaggio ?? 'La tua sessione è stata salvata.'}
      </p>
      <div className={styles.azioni}>
        <Button variante="ghost" onClick={onChiudi}>Chiudi</Button>
        <Button onClick={onVaiAllaSessione} disabled={!sessionId}>
          Vai al dettaglio
        </Button>
      </div>
    </div>
  )
}

// ─── Schermata errore ────────────────────────────────────────────────
function ErroreView({ messaggio, onChiudi }: { messaggio: string; onChiudi: () => void }) {
  return (
    <div className={styles.completata}>
      <div className={styles.iconaErrore}>
        <AlertCircle size={48} />
      </div>
      <h2>Si è verificato un errore</h2>
      <p className={styles.sottotitolo}>{messaggio}</p>
      <div className={styles.azioni}>
        <Button onClick={onChiudi}>Chiudi</Button>
      </div>
    </div>
  )
}

function etichettaStato(s: StatoSessione): string {
  switch (s) {
    case 'pronto': return 'Pronto'
    case 'ascolto': return 'Sto ascoltando'
    case 'elaborazione': return 'Sta pensando'
    case 'lead_parla': return 'Sta parlando'
    default: return s
  }
}
